import { useMutation } from "@tanstack/react-query";
import {
  type EmailDetail,
  type SendFailureDetails,
  type SentEmail,
  sendFailureDetailsSchema,
} from "@websede/contracts";
import { ArrowLeft, Maximize2, Minus, Paperclip, SendHorizontal, Trash2, X } from "lucide-react";
import { type DragEvent, type FormEvent, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiRequestError, api } from "../api/client.js";
import { ErrorNotice } from "../components/form.js";
import {
  invalidRecipients,
  parseRecipients,
  RecipientInput,
} from "../components/recipient-input.js";

/** Matches the API's MAIL_ATTACHMENT_MAX_BYTES default; the server has the final word. */
const MAX_ATTACHMENT_BYTES = 50 * 1024 * 1024;
const DRAFT_DELAY_MS = 2_000;

export interface ComposeAttachment {
  blobId: string;
  name: string;
  type: string;
  size: number;
}

export interface ComposeState {
  /** One of the mailbox's aliases; the mailbox's own address when absent. */
  from?: string;
  to: string;
  cc?: string;
  subject: string;
  text: string;
  inReplyTo?: string[];
  references?: string[];
  attachments?: ComposeAttachment[];
  /** The saved draft this message continues, replaced on every save. */
  draftId?: string;
}

export type ComposeUpdate = (update: (current: ComposeState) => ComposeState) => void;

/** The signature block, separated the conventional way ("-- " on its own line). */
export function signatureBlock(signature: string): string {
  return signature.trim() ? `\n\n-- \n${signature.trim()}` : "";
}

export function composeFromDraft(draft: EmailDetail): ComposeState {
  const from = draft.from[0]?.email;
  return {
    ...(from ? { from } : {}),
    to: draft.to.map((a) => a.email).join(", "),
    ...(draft.cc.length > 0 ? { cc: draft.cc.map((a) => a.email).join(", ") } : {}),
    subject: draft.subject,
    text: draft.textBody ?? "",
    ...(draft.inReplyTo ? { inReplyTo: draft.inReplyTo } : {}),
    attachments: draft.attachments.map((a) => ({
      blobId: a.blobId,
      name: a.name ?? "attachment",
      type: a.type,
      size: a.size,
    })),
    draftId: draft.id,
  };
}

/** What the API says about a send that left nothing: where the message waits, and who refused it. */
function sendFailureOf(error: unknown): SendFailureDetails | null {
  if (!(error instanceof ApiRequestError)) return null;
  const parsed = sendFailureDetailsSchema.safeParse(error.details);
  return parsed.success ? parsed.data : null;
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function isEmpty(state: ComposeState): boolean {
  return (
    !state.to.trim() &&
    !(state.cc ?? "").trim() &&
    !state.subject.trim() &&
    !state.text.trim() &&
    (state.attachments ?? []).length === 0
  );
}

/**
 * The composer. Files upload as they are added; the message is saved to Drafts a moment after
 * typing stops, one save at a time, so closing the tab never loses more than a few seconds.
 */
export function ComposePane({
  state,
  onChange,
  onClose,
  onSent,
  undoSeconds,
  senders = [],
}: {
  state: ComposeState;
  onChange: ComposeUpdate;
  onClose: () => void;
  onSent: (result: SentEmail) => void;
  undoSeconds: number;
  /** The mailbox's own address first, then its aliases; a choice appears with more than one. */
  senders?: string[];
}) {
  const { t } = useTranslation();
  const [showCc, setShowCc] = useState(Boolean(state.cc));
  const [uploads, setUploads] = useState<Array<{ key: number; name: string; error?: string }>>([]);
  const [draftStatus, setDraftStatus] = useState<"idle" | "saving" | "saved">(
    state.draftId ? "saved" : "idle",
  );
  const [dragging, setDragging] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);
  const uploadKey = useRef(0);
  const latest = useRef(state);
  latest.current = state;
  const sending = useRef(false);
  /** The draft save in flight; the next one, and sending, wait for it. */
  const saving = useRef<Promise<void>>(Promise.resolve());

  const saveDraft = (): Promise<void> => {
    saving.current = saving.current.then(async () => {
      const current = latest.current;
      if (sending.current || isEmpty(current)) return;
      setDraftStatus("saving");
      try {
        const cc = parseRecipients(current.cc ?? "");
        const saved = await api.mail.saveDraft({
          replaceId: current.draftId ?? null,
          ...(current.from ? { from: current.from } : {}),
          to: parseRecipients(current.to),
          ...(cc.length > 0 ? { cc } : {}),
          subject: current.subject,
          text: current.text,
          ...(current.inReplyTo ? { inReplyTo: current.inReplyTo } : {}),
          ...(current.references ? { references: current.references } : {}),
          attachments: (current.attachments ?? []).map(({ blobId, name, type }) => ({
            blobId,
            name,
            type,
          })),
        });
        latest.current = { ...latest.current, draftId: saved.id };
        onChange((c) => ({ ...c, draftId: saved.id }));
        setDraftStatus("saved");
      } catch {
        setDraftStatus("idle");
      }
    });
    return saving.current;
  };
  const saveDraftRef = useRef(saveDraft);
  saveDraftRef.current = saveDraft;

  // Save a moment after the content stops changing.
  const contentKey = JSON.stringify([
    state.from,
    state.to,
    state.cc,
    state.subject,
    state.text,
    (state.attachments ?? []).map((a) => a.blobId),
  ]);
  const lastKey = useRef(contentKey);
  useEffect(() => {
    // Nothing to save until the content differs from what the composer opened with.
    if (contentKey === lastKey.current) return;
    lastKey.current = contentKey;
    const timer = setTimeout(() => void saveDraftRef.current(), DRAFT_DELAY_MS);
    return () => clearTimeout(timer);
  }, [contentKey]);

  const addFiles = (files: FileList | File[]) => {
    for (const file of Array.from(files)) {
      const key = ++uploadKey.current;
      if (file.size > MAX_ATTACHMENT_BYTES) {
        setUploads((u) => [...u, { key, name: file.name, error: t("mail.tooLarge") }]);
        continue;
      }
      setUploads((u) => [...u, { key, name: file.name }]);
      api.mail
        .upload(file)
        .then((uploaded) => {
          setUploads((u) => u.filter((x) => x.key !== key));
          onChange((c) => ({ ...c, attachments: [...(c.attachments ?? []), uploaded] }));
        })
        .catch(() =>
          setUploads((u) =>
            u.map((x) => (x.key === key ? { ...x, error: t("mail.uploadFailed") } : x)),
          ),
        );
    }
  };

  const send = useMutation({
    mutationFn: async () => {
      sending.current = true;
      await saving.current;
      const current = latest.current;
      const cc = parseRecipients(current.cc ?? "");
      return api.mail.send({
        ...(current.from ? { from: current.from } : {}),
        to: parseRecipients(current.to),
        ...(cc.length > 0 ? { cc } : {}),
        subject: current.subject,
        text: current.text,
        ...(current.inReplyTo ? { inReplyTo: current.inReplyTo } : {}),
        ...(current.references ? { references: current.references } : {}),
        attachments: (current.attachments ?? []).map(({ blobId, name, type }) => ({
          blobId,
          name,
          type,
        })),
        ...(current.draftId ? { draftId: current.draftId } : {}),
        undoSeconds,
      });
    },
    onSuccess: onSent,
    onError: (error) => {
      sending.current = false;
      // Nothing left, and the message now waits in Drafts under a new id: keep editing that one,
      // so the next save replaces it instead of adding a second draft.
      const failure = sendFailureOf(error);
      if (failure) {
        latest.current = { ...latest.current, draftId: failure.draftId };
        onChange((c) => ({ ...c, draftId: failure.draftId }));
        setDraftStatus("saved");
      }
    },
  });
  const refusedAll = sendFailureOf(send.error)?.refused ?? [];
  const discard = useMutation({
    mutationFn: async () => {
      sending.current = true;
      await saving.current;
      const id = latest.current.draftId;
      if (id) await api.mail.discardDraft(id);
    },
    onSettled: onClose,
  });

  const [problem, setProblem] = useState<string | null>(null);
  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (uploads.some((u) => !u.error)) return;
    const current = latest.current;
    const wrong = [...invalidRecipients(current.to), ...invalidRecipients(current.cc ?? "")];
    if (parseRecipients(current.to).length === 0) {
      setProblem(t("mail.needRecipient"));
      return;
    }
    if (wrong.length > 0) {
      setProblem(t("mail.invalidRecipient", { value: wrong.join(", ") }));
      return;
    }
    setProblem(null);
    send.mutate();
  };
  const onDrop = (event: DragEvent<HTMLFormElement>) => {
    event.preventDefault();
    setDragging(false);
    if (event.dataTransfer.files.length > 0) addFiles(event.dataTransfer.files);
  };
  const uploading = uploads.some((u) => !u.error);

  const closeWindow = () => {
    void saveDraftRef.current().then(onClose);
  };
  const title = state.subject.trim() || t("mail.newMessage");

  if (minimized) {
    return (
      <div className="fixed right-6 bottom-0 z-40 hidden w-[280px] md:block">
        <div className="flex h-10 items-center gap-2 rounded-t-xl bg-surface-2 pl-4 shadow-2">
          <button
            type="button"
            className="min-w-0 flex-1 truncate text-left text-sm font-medium"
            onClick={() => setMinimized(false)}
          >
            {title}
          </button>
          <button
            type="button"
            aria-label={t("mail.expand")}
            className="flex size-8 items-center justify-center rounded-full hover:bg-ink/5"
            onClick={() => setMinimized(false)}
          >
            <Maximize2 className="size-4" />
          </button>
          <button
            type="button"
            aria-label={t("common.close")}
            className="mr-1 flex size-8 items-center justify-center rounded-full hover:bg-ink/5"
            onClick={closeWindow}
          >
            <X className="size-4" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={onSubmit}
      onDragOver={(event) => {
        if (event.dataTransfer.types.includes("Files")) {
          event.preventDefault();
          setDragging(true);
        }
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={onDrop}
      className={`fixed inset-0 z-50 flex flex-col bg-surface md:inset-auto md:right-6 md:bottom-0 md:z-40 md:h-[min(620px,calc(100vh-96px))] md:w-[560px] md:rounded-t-xl md:shadow-2 ${dragging ? "outline-2 outline-accent outline-dashed" : ""}`}
    >
      <div className="flex h-14 shrink-0 items-center gap-1 px-2 md:h-10 md:rounded-t-xl md:bg-surface-2 md:pl-4">
        <button
          type="button"
          aria-label={t("common.close")}
          className="flex size-10 items-center justify-center rounded-full hover:bg-ink/5 md:hidden"
          onClick={closeWindow}
        >
          <ArrowLeft className="size-5" />
        </button>
        <span className="min-w-0 flex-1 truncate text-base md:text-sm md:font-medium">{title}</span>
        <span className="hidden shrink-0 text-xs text-ink-3 sm:inline">
          {draftStatus === "saving"
            ? t("mail.draftSaving")
            : draftStatus === "saved"
              ? t("mail.draftSaved")
              : ""}
        </span>
        <button
          type="button"
          aria-label={t("mail.minimize")}
          className="hidden size-8 items-center justify-center rounded-full hover:bg-ink/5 md:flex"
          onClick={() => setMinimized(true)}
        >
          <Minus className="size-4" />
        </button>
        <button
          type="button"
          aria-label={t("common.close")}
          className="hidden size-8 items-center justify-center rounded-full hover:bg-ink/5 md:flex"
          onClick={closeWindow}
        >
          <X className="size-4" />
        </button>
        <button
          type="button"
          aria-label={t("mail.attach")}
          className="flex size-10 items-center justify-center rounded-full hover:bg-ink/5 md:hidden"
          onClick={() => fileInput.current?.click()}
        >
          <Paperclip className="size-5" />
        </button>
        <button
          type="submit"
          aria-label={t("mail.send")}
          disabled={send.isPending || uploading}
          className="flex size-10 items-center justify-center rounded-full text-accent hover:bg-ink/5 disabled:opacity-50 md:hidden"
        >
          <SendHorizontal className="size-5" />
        </button>
      </div>
      <div className="flex min-h-0 flex-1 flex-col px-4">
        {senders.length > 1 ? (
          <label className="flex items-center gap-3 border-b border-rule">
            <span className="w-10 shrink-0 text-sm text-ink-3">{t("mail.from")}</span>
            <select
              aria-label={t("mail.from")}
              value={state.from && senders.includes(state.from) ? state.from : senders[0]}
              onChange={(e) => onChange((c) => ({ ...c, from: e.target.value }))}
              className="min-w-0 flex-1 cursor-pointer bg-transparent py-2.5 text-sm text-ink outline-none"
            >
              {senders.map((address) => (
                <option key={address} value={address}>
                  {address}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        <div className="flex items-center gap-3 border-b border-rule">
          <span className="w-10 shrink-0 text-sm text-ink-3">{t("mail.to")}</span>
          <RecipientInput
            bare
            required
            label={t("mail.to")}
            placeholder={t("mail.recipientsPlaceholder")}
            autoFocus={!state.to}
            value={state.to}
            onChange={(to) => {
              setProblem(null);
              onChange((c) => ({ ...c, to }));
            }}
          />
          {showCc ? null : (
            <button
              type="button"
              className="shrink-0 text-sm text-ink-3 hover:text-ink"
              onClick={() => setShowCc(true)}
            >
              {t("mail.cc")}
            </button>
          )}
        </div>
        {showCc ? (
          <div className="flex items-center gap-3 border-b border-rule">
            <span className="w-10 shrink-0 text-sm text-ink-3">{t("mail.cc")}</span>
            <RecipientInput
              bare
              label={t("mail.cc")}
              autoFocus={!state.cc}
              value={state.cc ?? ""}
              onChange={(cc) => onChange((c) => ({ ...c, cc }))}
            />
          </div>
        ) : null}
        <input
          aria-label={t("mail.subject")}
          placeholder={t("mail.subject")}
          className="border-b border-rule bg-transparent py-2 text-sm outline-none"
          value={state.subject}
          onChange={(e) => {
            const subject = e.target.value;
            onChange((c) => ({ ...c, subject }));
          }}
        />
        <textarea
          aria-label={t("mail.body")}
          className="min-h-40 flex-1 resize-none bg-transparent py-3 text-sm leading-relaxed outline-none"
          value={state.text}
          onChange={(e) => {
            const text = e.target.value;
            onChange((c) => ({ ...c, text }));
          }}
        />
        {(state.attachments ?? []).length > 0 || uploads.length > 0 ? (
          <ul className="flex max-h-28 flex-wrap gap-2 overflow-auto pb-2 text-xs">
            {(state.attachments ?? []).map((a) => (
              <li
                key={a.blobId}
                className="flex items-center gap-2 rounded-full bg-surface-2 py-1 pr-1 pl-3"
              >
                <Paperclip className="size-3.5 text-ink-3" />
                <span className="max-w-48 truncate">{a.name}</span>
                <span className="text-ink-3">{formatSize(a.size)}</span>
                <button
                  type="button"
                  className="flex size-6 items-center justify-center rounded-full text-ink-3 hover:bg-ink/5 hover:text-danger"
                  aria-label={t("mail.removeAttachment", { name: a.name })}
                  onClick={() =>
                    onChange((c) => ({
                      ...c,
                      attachments: (c.attachments ?? []).filter((x) => x.blobId !== a.blobId),
                    }))
                  }
                >
                  <X className="size-3.5" />
                </button>
              </li>
            ))}
            {uploads.map((u) => (
              <li
                key={u.key}
                className={`flex items-center gap-2 rounded-full py-1 pr-1 pl-3 ${u.error ? "bg-danger-soft text-danger" : "bg-surface-2 text-ink-3"}`}
              >
                <span className="max-w-48 truncate">{u.name}</span>
                <span>{u.error ?? t("mail.uploading")}</span>
                {u.error ? (
                  <button
                    type="button"
                    className="flex size-6 items-center justify-center rounded-full hover:bg-ink/5"
                    aria-label={t("mail.removeAttachment", { name: u.name })}
                    onClick={() => setUploads((list) => list.filter((x) => x.key !== u.key))}
                  >
                    <X className="size-3.5" />
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        ) : null}
        {problem ? (
          <p role="alert" className="rounded-lg bg-danger-soft px-3 py-2 text-sm text-danger">
            {problem}
          </p>
        ) : null}
        {refusedAll.length > 0 ? (
          <p role="alert" className="rounded-lg bg-danger-soft px-3 py-2 text-sm text-danger">
            {t("mail.notSentRefused", { recipients: refusedAll.map((r) => r.email).join(", ") })}
          </p>
        ) : (
          <ErrorNotice error={send.error ?? discard.error} />
        )}
      </div>
      <div className="hidden shrink-0 items-center gap-1 px-4 py-3 md:flex">
        <button
          type="submit"
          disabled={send.isPending || uploading}
          className="h-10 rounded-full bg-accent px-6 text-sm font-medium text-white hover:shadow-1 disabled:opacity-60"
        >
          {send.isPending ? t("common.loading") : t("mail.send")}
        </button>
        <button
          type="button"
          aria-label={t("mail.attach")}
          title={t("mail.dropFiles")}
          className="ml-2 flex size-10 items-center justify-center rounded-full text-ink-2 hover:bg-ink/5"
          onClick={() => fileInput.current?.click()}
        >
          <Paperclip className="size-5" />
        </button>
        <span className="flex-1" />
        <button
          type="button"
          aria-label={t("mail.discard")}
          title={t("mail.discard")}
          disabled={discard.isPending}
          className="flex size-10 items-center justify-center rounded-full text-ink-2 hover:bg-ink/5 hover:text-danger"
          onClick={() => discard.mutate()}
        >
          <Trash2 className="size-5" />
        </button>
      </div>
      <button
        type="button"
        className="flex h-12 shrink-0 items-center justify-center gap-2 border-t border-rule text-sm text-ink-2 md:hidden"
        disabled={discard.isPending}
        onClick={() => discard.mutate()}
      >
        <Trash2 className="size-4" />
        {t("mail.discard")}
      </button>
      <input
        ref={fileInput}
        type="file"
        multiple
        hidden
        onChange={(e) => {
          if (e.target.files) addFiles(e.target.files);
          e.target.value = "";
        }}
      />
    </form>
  );
}

/**
 * Shown after sending: while the message is held, a countdown with a way to take it back; and
 * the recipients the mail server refused, when it accepted only some of them.
 */
export function UndoSendBar({
  submissionId,
  sendAt,
  refused = [],
  onUndone,
  onDismiss,
}: {
  /** Null when the message was not held: nothing to take back. */
  submissionId: string | null;
  sendAt: string | null;
  refused?: string[];
  onUndone: (draftId: string) => void;
  onDismiss: () => void;
}) {
  const { t } = useTranslation();
  const [now, setNow] = useState(() => Date.now());
  const remaining = sendAt ? Math.max(0, Math.ceil((Date.parse(sendAt) - now) / 1000)) : 0;
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(timer);
  }, []);
  useEffect(() => {
    if (remaining > 0) return;
    // Long enough to read who did not get it.
    const timer = setTimeout(onDismiss, refused.length > 0 ? 10_000 : 3_000);
    return () => clearTimeout(timer);
  }, [remaining, onDismiss, refused.length]);
  const undo = useMutation({
    mutationFn: () => api.mail.undoSend(submissionId ?? ""),
    onSuccess: (result) => onUndone(result.draftId),
  });
  return (
    <div
      className="fixed inset-x-4 bottom-20 z-50 flex items-center gap-4 rounded-lg bg-[#303030] px-4 py-3 text-sm text-white shadow-2 md:inset-x-auto md:bottom-6 md:left-6 md:min-w-[320px]"
      role="status"
    >
      <div className="flex min-w-0 flex-col">
        <span>
          {remaining > 0 ? t("mail.sendingIn", { seconds: remaining }) : t("mail.sentNow")}
        </span>
        {refused.length > 0 ? (
          <span className="break-words text-[#f2b8b5]">
            {t("mail.notDeliveredTo", { recipients: refused.join(", ") })}
          </span>
        ) : null}
      </div>
      {remaining > 0 && submissionId ? (
        <button
          type="button"
          className="ml-auto font-medium text-[#8fd8d2] hover:underline"
          disabled={undo.isPending}
          onClick={() => undo.mutate()}
        >
          {t("mail.undo")}
        </button>
      ) : null}
      {undo.error ? <span className="text-[#f2b8b5]">{t("mail.tooLate")}</span> : null}
    </div>
  );
}
